/**
 * Журнал записи бага: события отладчика Chrome (их пересылает расширение
 * «Запись бага») → HAR 1.2 и текст консоли.
 *
 * HAR — общепринятый формат: его открывают DevTools (вкладка Network,
 * «Import HAR») и просмотр в карточке задачи. Тела ответов сохраняются
 * все: текстовые — текстом, двоичные (картинки, шрифты) — в base64,
 * как велит HAR (content.encoding).
 *
 * Журнал уходит в задачу, которую читают все участники доски, поэтому
 * секреты вычищаются до загрузки: заголовки авторизации и cookie, поля
 * с паролями и токенами в адресе, теле запроса и JSON-ответах.
 */

export const HIDDEN = "[скрыто]";

const SECRET_HEADERS = new Set([
  "authorization",
  "proxy-authorization",
  "cookie",
  "set-cookie",
  "x-api-key",
  "x-auth-token",
  "x-access-token",
  "x-csrf-token",
  "x-xsrf-token",
]);

const SECRET_NAME = /pass(word)?|passwd|pwd|token|secret|authorization|api[-_]?key|cookie|session|credential/i;

/** Текстовое тело длиннее этого обрезается. */
export const BODY_MAX_CHARS = 256 * 1024;
/** Двоичное тело (base64) длиннее этого не сохраняется: обрезанная
 *  картинка бесполезна. Около 1 МБ исходных байтов. */
export const BINARY_MAX_CHARS = 1_400_000;
/** Все тела вместе — запас до предела вложения в 25 МБ. */
export const BODIES_BUDGET_CHARS = 18 * 1024 * 1024;
/** Как BODY_LIMIT в apps/extension/background.js: тела больших ответов
 *  расширение не запрашивает. */
export const EXTENSION_BODY_LIMIT = 512 * 1024;

type Headers = Record<string, string>;

interface CdpRequest {
  url: string;
  method: string;
  headers?: Headers;
  postData?: string;
  hasPostData?: boolean;
}

interface CdpResponse {
  url?: string;
  status: number;
  statusText?: string;
  headers?: Headers;
  mimeType?: string;
  protocol?: string;
  remoteIPAddress?: string;
  fromDiskCache?: boolean;
  fromServiceWorker?: boolean;
  encodedDataLength?: number;
}

interface Entry {
  requestId: string;
  /** Время начала по часам пользователя, мс. */
  wallMs: number;
  /** Монотонное время отладчика, с: начало и конец. */
  start: number;
  end: number | null;
  type: string;
  request: CdpRequest;
  response: CdpResponse | null;
  failure: string | null;
  size: number;
  body: { text: string; base64: boolean; truncated: boolean } | null;
  /** Почему тело не сохранено, если причина известна. */
  bodyNote: string | null;
  initiator: string | null;
}

export interface ConsoleLine {
  /** Мс от начала записи. */
  offsetMs: number;
  level: string;
  text: string;
  source: string | null;
}

interface RemoteObject {
  type?: string;
  subtype?: string;
  value?: unknown;
  unserializableValue?: string;
  description?: string;
}

interface CallFrame {
  url?: string;
  lineNumber?: number;
}

export interface CdpEvent {
  method: string;
  params: Record<string, unknown>;
}

function maskHeaders(headers: Headers | undefined): { name: string; value: string }[] {
  return Object.entries(headers ?? {}).map(([name, value]) => ({
    name,
    value: SECRET_HEADERS.has(name.toLowerCase()) ? HIDDEN : String(value),
  }));
}

export function maskUrl(raw: string): string {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return raw;
  }
  if (url.username !== "" || url.password !== "") {
    url.username = "";
    url.password = "";
  }
  for (const key of [...url.searchParams.keys()]) {
    if (SECRET_NAME.test(key)) url.searchParams.set(key, HIDDEN);
  }
  return url.toString();
}

function maskJsonValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(maskJsonValue);
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        SECRET_NAME.test(key) && (typeof item === "string" || typeof item === "number")
          ? HIDDEN
          : maskJsonValue(item),
      ]),
    );
  }
  return value;
}

/** Тело (запроса или ответа) без секретов. JSON и формы разбираются,
 *  остальное оставляется как есть. */
export function maskBody(text: string, mimeType: string): string {
  const mime = mimeType.toLowerCase();
  if (mime.includes("json") || /^\s*[{[]/.test(text)) {
    try {
      return JSON.stringify(maskJsonValue(JSON.parse(text)));
    } catch {
      // Не JSON, хоть и похоже, — дальше как обычный текст.
    }
  }
  if (mime.includes("x-www-form-urlencoded")) {
    const form = new URLSearchParams(text);
    for (const key of [...form.keys()]) if (SECRET_NAME.test(key)) form.set(key, HIDDEN);
    return form.toString();
  }
  return text;
}

function headerValue(headers: Headers | undefined, name: string): string {
  const found = Object.entries(headers ?? {}).find(([key]) => key.toLowerCase() === name);
  return found === undefined ? "" : String(found[1]);
}

function remoteText(arg: RemoteObject): string {
  if (arg.type === "string" && typeof arg.value === "string") return arg.value;
  if (arg.unserializableValue !== undefined) return arg.unserializableValue;
  if (arg.value !== undefined) {
    try {
      return JSON.stringify(arg.value);
    } catch {
      return arg.description ?? "[объект]";
    }
  }
  return arg.description ?? arg.type ?? "";
}

function place(url: string | undefined, line: number | undefined): string | null {
  if (url === undefined || url === "") return null;
  return line === undefined ? maskUrl(url) : `${maskUrl(url)}:${String(line + 1)}`;
}

function clock(ms: number): string {
  const safe = Math.max(0, Math.round(ms));
  const minutes = Math.floor(safe / 60000);
  const seconds = Math.floor((safe % 60000) / 1000);
  const millis = safe % 1000;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}.${String(millis).padStart(3, "0")}`;
}

const asString = (value: unknown): string => (typeof value === "string" ? value : "");
const asNumber = (value: unknown): number => (typeof value === "number" ? value : 0);

export class NetworkLog {
  private readonly entries: Entry[] = [];
  private readonly open = new Map<string, Entry>();
  private readonly lines: ConsoleLine[] = [];
  private bodyChars = 0;

  /** startedAt — момент начала записи (Date.now()), от него считаются смещения. */
  constructor(private readonly startedAt: number) {}

  get requestCount(): number {
    return this.entries.length;
  }

  get errorCount(): number {
    return (
      this.entries.filter((entry) => entry.failure !== null || (entry.response?.status ?? 0) >= 400)
        .length + this.lines.filter((line) => line.level === "error").length
    );
  }

  add({ method, params }: CdpEvent): void {
    switch (method) {
      case "Network.requestWillBeSent":
        this.requestWillBeSent(params);
        return;
      case "Network.responseReceived": {
        const entry = this.open.get(asString(params["requestId"]));
        if (entry !== undefined) entry.response = params["response"] as CdpResponse;
        return;
      }
      case "Network.loadingFinished": {
        const entry = this.open.get(asString(params["requestId"]));
        if (entry === undefined) return;
        entry.end = asNumber(params["timestamp"]);
        entry.size = asNumber(params["encodedDataLength"]);
        return;
      }
      case "Network.loadingFailed": {
        const entry = this.open.get(asString(params["requestId"]));
        if (entry === undefined) return;
        entry.end = asNumber(params["timestamp"]);
        entry.failure =
          params["canceled"] === true ? "Отменён" : asString(params["errorText"]) || "Ошибка сети";
        return;
      }
      case "Runtime.consoleAPICalled":
        this.consoleCalled(params);
        return;
      case "Runtime.exceptionThrown":
        this.exception(params);
        return;
      case "Log.entryAdded":
        this.logEntry(params);
        return;
      default:
        return;
    }
  }

  addBody(requestId: string, body: string, base64Encoded: boolean): void {
    const entry = this.open.get(requestId);
    // Пустое тело: либо ответ и правда пуст, либо Chrome не отдал его
    // отладчику (страница читала ответ как Blob) — решает harContent.
    if (entry === undefined || body === "") return;
    if (base64Encoded && body.length > BINARY_MAX_CHARS) {
      entry.bodyNote = `Тело не сохранено: двоичный ответ больше ${String(Math.round((BINARY_MAX_CHARS * 3) / 4 / 1024))} КБ`;
      return;
    }
    const truncated = !base64Encoded && body.length > BODY_MAX_CHARS;
    const text = truncated ? body.slice(0, BODY_MAX_CHARS) : body;
    if (this.bodyChars + text.length > BODIES_BUDGET_CHARS) {
      entry.bodyNote = "Тело не сохранено: журнал достиг предельного объёма тел ответов";
      return;
    }
    this.bodyChars += text.length;
    entry.body = { text, base64: base64Encoded, truncated };
  }

  private requestWillBeSent(params: Record<string, unknown>): void {
    const requestId = asString(params["requestId"]);
    const timestamp = asNumber(params["timestamp"]);
    const previous = this.open.get(requestId);
    // Перенаправление приходит тем же requestId: прошлый шаг закрывается
    // ответом-перенаправлением, а новый адрес начинает свою запись.
    if (previous !== undefined && params["redirectResponse"] !== undefined) {
      previous.response = params["redirectResponse"] as CdpResponse;
      previous.end = timestamp;
    }
    const initiator = params["initiator"] as { type?: string; url?: string; lineNumber?: number } | undefined;
    const entry: Entry = {
      requestId,
      wallMs: asNumber(params["wallTime"]) * 1000,
      start: timestamp,
      end: null,
      type: asString(params["type"]) || "Other",
      request: params["request"] as CdpRequest,
      response: null,
      failure: null,
      size: 0,
      body: null,
      bodyNote: null,
      initiator: initiator === undefined ? null : (place(initiator.url, initiator.lineNumber) ?? initiator.type ?? null),
    };
    this.entries.push(entry);
    this.open.set(requestId, entry);
  }

  private consoleCalled(params: Record<string, unknown>): void {
    const args = (params["args"] as RemoteObject[] | undefined) ?? [];
    const frame = ((params["stackTrace"] as { callFrames?: CallFrame[] } | undefined)?.callFrames ?? [])[0];
    const type = asString(params["type"]);
    this.lines.push({
      offsetMs: asNumber(params["timestamp"]) - this.startedAt,
      level: type === "warning" ? "warn" : type || "log",
      text: args.map(remoteText).join(" "),
      source: place(frame?.url, frame?.lineNumber),
    });
  }

  private exception(params: Record<string, unknown>): void {
    const details = (params["exceptionDetails"] ?? {}) as {
      text?: string;
      url?: string;
      lineNumber?: number;
      exception?: RemoteObject;
    };
    this.lines.push({
      offsetMs: asNumber(params["timestamp"]) - this.startedAt,
      level: "error",
      text: details.exception?.description ?? details.text ?? "Исключение",
      source: place(details.url, details.lineNumber),
    });
  }

  private logEntry(params: Record<string, unknown>): void {
    const entry = (params["entry"] ?? {}) as {
      level?: string;
      text?: string;
      url?: string;
      lineNumber?: number;
      timestamp?: number;
    };
    this.lines.push({
      offsetMs: (entry.timestamp ?? this.startedAt) - this.startedAt,
      level: entry.level === "warning" ? "warn" : (entry.level ?? "info"),
      text: entry.text ?? "",
      source: place(entry.url, entry.lineNumber),
    });
  }

  consoleLines(): ConsoleLine[] {
    return [...this.lines].sort((a, b) => a.offsetMs - b.offsetMs);
  }

  /** Журнал консоли текстом: «[мм:сс.ммм] LEVEL текст (адрес:строка)». */
  consoleText(header: string): string {
    const body = this.consoleLines().map(
      (line) =>
        `[${clock(line.offsetMs)}] ${line.level.toUpperCase().padEnd(5)} ${line.text}${line.source === null ? "" : `  (${line.source})`}`,
    );
    return [header, "", ...(body.length === 0 ? ["Сообщений в консоли не было."] : body), ""].join("\n");
  }

  toHar(page: { title: string; url: string }, creatorVersion: string): object {
    const pageId = "page_1";
    return {
      log: {
        version: "1.2",
        creator: { name: "Задачник: запись бага", version: creatorVersion },
        pages: [
          {
            startedDateTime: new Date(this.startedAt).toISOString(),
            id: pageId,
            title: page.title || maskUrl(page.url),
            pageTimings: {},
          },
        ],
        entries: this.entries.map((entry) => this.harEntry(entry, pageId)),
      },
    };
  }

  /** Тело ответа для HAR: текст, base64 или пояснение, почему его нет. */
  private harContent(entry: Entry, mime: string): Record<string, string> {
    const { body } = entry;
    if (body !== null && body.base64) return { text: body.text, encoding: "base64" };
    if (body !== null) {
      return {
        text: maskBody(body.text, mime),
        ...(body.truncated
          ? { comment: `Тело обрезано до ${String(BODY_MAX_CHARS / 1024)} КБ` }
          : {}),
      };
    }
    if (entry.bodyNote !== null) return { comment: entry.bodyNote };
    const status = entry.response?.status ?? 0;
    // Нет ответа, перенаправление или пустой ответ — пояснять нечего.
    if (entry.failure !== null || entry.response === null || entry.end === null) return {};
    if ((status >= 300 && status < 400) || status === 204 || entry.size === 0) return {};
    if (headerValue(entry.response.headers, "content-length") === "0") return {};
    if (entry.size > EXTENSION_BODY_LIMIT) {
      return { comment: `Тело не сохранено: ответ больше ${String(EXTENSION_BODY_LIMIT / 1024)} КБ` };
    }
    return {
      comment:
        "Тело не сохранено: браузер не отдал его отладчику (ответ из кэша, потоковый или прочитан страницей как Blob)",
    };
  }

  private harEntry(entry: Entry, pageId: string): object {
    const { request, response } = entry;
    const url = maskUrl(request.url);
    const total = entry.end === null ? -1 : Math.max(0, (entry.end - entry.start) * 1000);
    const requestMime = headerValue(request.headers, "content-type");
    const responseMime = response?.mimeType ?? "";
    let query: { name: string; value: string }[] = [];
    try {
      query = [...new URL(url).searchParams].map(([name, value]) => ({ name, value }));
    } catch {
      // Адрес без схемы (data:, blob:) — параметров нет.
    }
    return {
      pageref: pageId,
      startedDateTime: new Date(entry.wallMs || this.startedAt).toISOString(),
      time: total,
      _resourceType: entry.type.toLowerCase(),
      ...(entry.initiator === null ? {} : { _initiator: entry.initiator }),
      ...(entry.failure === null ? {} : { _error: entry.failure }),
      request: {
        method: request.method,
        url,
        httpVersion: response?.protocol ?? "",
        cookies: [],
        headers: maskHeaders(request.headers),
        queryString: query,
        headersSize: -1,
        bodySize: request.postData?.length ?? 0,
        ...(request.postData === undefined
          ? {}
          : { postData: { mimeType: requestMime, text: maskBody(request.postData, requestMime) } }),
      },
      response: {
        status: entry.failure !== null && response === null ? 0 : (response?.status ?? 0),
        statusText: entry.failure ?? response?.statusText ?? "",
        httpVersion: response?.protocol ?? "",
        cookies: [],
        headers: maskHeaders(response?.headers),
        content: {
          size: entry.size,
          mimeType: responseMime,
          ...this.harContent(entry, responseMime),
        },
        redirectURL: headerValue(response?.headers, "location"),
        headersSize: -1,
        bodySize: entry.size || -1,
      },
      cache: {},
      timings: { send: 0, wait: total < 0 ? -1 : total, receive: 0 },
      ...(response?.remoteIPAddress === undefined ? {} : { serverIPAddress: response.remoteIPAddress }),
    };
  }
}
